import { useCallback, useEffect, useMemo, useState } from 'react';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina, FaixaFiltros } from '../components/layout/CabecalhoPagina';
import { EstadoVazio, EstadoCarregando } from '../components/common/Estados';
import { BarraAcoes } from '../components/common/Formulario';
import { teamApi, federationApi, fileApi, userApi, equipeSolicitacaoApi } from '../lib/api';
import DialogoMotivo from '../components/common/DialogoMotivo';
import { useAuth } from '../contexts/AuthContext';
import { ImageUpload } from '../components/common/ImageUpload';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Shield, 
  Plus, 
  Search, 
  Loader2,
  Edit,
  Trash2,
  MapPin,
  Users,
  Medal,
  X,
  Check,
  ShieldPlus,
} from 'lucide-react';
import { toast } from 'sonner';

export default function TeamsPage() {
  const { user } = useAuth();
  const isProfessor = user?.role === 'professor';
  const canManage = ['super_admin', 'federation'].includes(user?.role);
  const [teams, setTeams] = useState([]);
  const [federations, setFederations] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  /* PEDIDOS DE EQUIPE — fila de quem aprova (super_admin/federacao) */
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [recusando, setRecusando] = useState(null);
  const [processando, setProcessando] = useState('');
  const [editingTeam, setEditingTeam] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingLogo, setPendingLogo] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    federation_ids: [],
    president_id: '',
    professor_ids: [],
    logo_path: '',
  });

  const fetchData = async () => {
    try {
      const [teamsRes, fedRes, profRes] = await Promise.all([
        teamApi.list(),
        federationApi.list(),
        userApi.list({ role: 'professor' }),
      ]);
      // Professors only see teams where they are president, member, or via legacy team_id
      if (isProfessor && user?._id) {
        const myId = user._id;
        const legacyId = user?.team_id;
        setTeams(teamsRes.data.filter(t =>
          t.president_id === myId ||
          (Array.isArray(t.professor_ids) && t.professor_ids.includes(myId)) ||
          (legacyId && t._id === legacyId)
        ));
      } else {
        setTeams(teamsRes.data);
      }
      setFederations(fedRes.data);
      setProfessors(profRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* FILA DE PEDIDOS DE EQUIPE: o lider PEDE; a federacao (os destinados a ela)
     ou o super_admin aprova. A equipe nasce SOMENTE na aprovacao. */
  const carregarSolicitacoes = useCallback(async () => {
    if (!canManage) return;
    try {
      const { data } = await equipeSolicitacaoApi.listar();
      setSolicitacoes(data?.solicitacoes || []);
    } catch (e) { /* silencioso: a faixa simplesmente nao aparece */ }
  }, [canManage]);

  useEffect(() => { carregarSolicitacoes(); }, [carregarSolicitacoes]);

  const aprovarPedido = async (id) => {
    setProcessando(id);
    try {
      await equipeSolicitacaoApi.aprovar(id);
      toast.success('Pedido aprovado — equipe criada!');
      await carregarSolicitacoes();
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao aprovar o pedido');
    } finally {
      setProcessando('');
    }
  };

  const recusarPedido = async (motivo) => {
    const alvo = recusando;
    setRecusando(null);
    if (!alvo) return;
    setProcessando(alvo._id);
    try {
      await equipeSolicitacaoApi.recusar(alvo._id, motivo);
      toast.success('Pedido recusado');
      await carregarSolicitacoes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao recusar o pedido');
    } finally {
      setProcessando('');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      federation_ids: [],
      president_id: '',
      professor_ids: [],
      logo_path: '',
    });
    setEditingTeam(null);
    setPendingLogo(null);
  };

  const handleEdit = (team) => {
    // Backward-compat: derive president/professor_ids from legacy fields when needed
    const legacyProf = team.professor_id || '';
    const legacyMonitors = team.monitors || [];
    setFormData({
      name: team.name || '',
      address: team.address || '',
      federation_ids: team.federation_ids || [],
      president_id: team.president_id || legacyProf,
      professor_ids: team.professor_ids?.length
        ? team.professor_ids
        : [...new Set([legacyProf, ...legacyMonitors].filter(Boolean))],
      logo_path: team.logo_path || '',
    });
    setEditingTeam(team);
    setPendingLogo(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta equipe?')) return;
    
    try {
      await teamApi.delete(id);
      toast.success('Equipe excluída com sucesso');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir equipe');
    }
  };

  const handleLogoUpload = (pathOrFile) => {
    if (pathOrFile instanceof File) {
      setPendingLogo(pathOrFile);
    } else {
      setFormData(prev => ({ ...prev, logo_path: pathOrFile }));
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);

    try {
      let result;
      if (editingTeam) {
        await teamApi.update(editingTeam._id, formData);
        result = { _id: editingTeam._id };
        toast.success('Equipe atualizada com sucesso');
      } else {
        const { data: newTeam } = await teamApi.create(formData);
        result = newTeam;
        toast.success('Equipe criada com sucesso');
      }

      // Upload pending logo if exists
      if (pendingLogo && result._id) {
        try {
          await fileApi.upload(pendingLogo, 'team', result._id);
        } catch (err) {
          toast.error('Erro ao enviar logo');
        }
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(editingTeam ? 'Erro ao atualizar equipe' : 'Erro ao criar equipe');
    } finally {
      setSaving(false);
    }
  };

  const filteredTeams = teams.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="teams-page">
        {/* Header — título/contexto à esquerda, ação à direita */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <CabecalhoPagina
            className="min-w-0 flex-1"
            contexto="Cadastros"
            titulo="Equipes"
            descricao="Gerencie as equipes de Jiu-Jitsu"
            icone={Shield}
          />
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            {canManage && (
              <DialogTrigger asChild>
                <Button
                  data-testid="new-team-btn"
                  className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold"
                >
                  <Plus size={20} className="mr-2" />
                  Nova Equipe
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="bg-[#141414] border-[#27272A] w-[95vw] sm:max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
              <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#27272A]">
                <DialogTitle className="text-white font-['Chivo']">
                  {editingTeam ? 'Editar Equipe' : 'Nova Equipe'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Nome da Equipe *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Alliance BJJ"
                    required
                    data-testid="team-name"
                    className="bg-[#1A1A1A] border-[#27272A] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Endereço da Sede</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Endereço completo"
                    data-testid="team-address"
                    className="bg-[#1A1A1A] border-[#27272A] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Federações Vinculadas</Label>
                  <p className="text-xs text-[#71717A]">Uma equipe pode ser federada por mais de uma federação. Selecione todas as aplicáveis.</p>
                  <div className="border border-[#27272A] rounded-md bg-[#1A1A1A] p-3 space-y-2 max-h-40 overflow-y-auto">
                    {federations.length === 0 && <p className="text-sm text-[#71717A]">Nenhuma federação cadastrada</p>}
                    {federations.map((fed) => {
                      const checked = formData.federation_ids.includes(fed._id);
                      return (
                        <label key={fed._id} className="flex items-center gap-2 cursor-pointer text-white text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...formData.federation_ids, fed._id]
                                : formData.federation_ids.filter(id => id !== fed._id);
                              setFormData({ ...formData, federation_ids: next });
                            }}
                            className="w-4 h-4 accent-[#D4AF37]"
                            data-testid={`team-fed-${fed._id}`}
                          />
                          {fed.name}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Professores da Equipe</Label>
                  <p className="text-xs text-[#71717A]">Marque todos os professores que dão aula nesta equipe.</p>
                  <div className="border border-[#27272A] rounded-md bg-[#1A1A1A] p-3 space-y-2 max-h-48 overflow-y-auto">
                    {professors.length === 0 && <p className="text-sm text-[#71717A]">Nenhum professor cadastrado</p>}
                    {professors.map((p) => {
                      const checked = formData.professor_ids.includes(p._id);
                      return (
                        <label key={p._id} className="flex items-center gap-2 cursor-pointer text-white text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...formData.professor_ids, p._id]
                                : formData.professor_ids.filter(id => id !== p._id);
                              // If removing the current president, clear it
                              const newPresident = !e.target.checked && formData.president_id === p._id ? '' : formData.president_id;
                              setFormData({ ...formData, professor_ids: next, president_id: newPresident });
                            }}
                            className="w-4 h-4 accent-[#D4AF37]"
                            data-testid={`team-prof-${p._id}`}
                          />
                          <span>{p.name}</span>
                          <span className="text-xs text-[#71717A]">({p.email})</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Presidente / Responsável *</Label>
                  <p className="text-xs text-[#71717A]">Líder administrativo. Deve ser um dos professores marcados acima.</p>
                  <Select
                    value={formData.president_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, president_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="team-president" className="bg-[#1A1A1A] border-[#27272A] text-white">
                      <SelectValue placeholder="Selecione o presidente" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value="none" className="text-white">Nenhum</SelectItem>
                      {professors
                        .filter(p => formData.professor_ids.includes(p._id))
                        .map((p) => (
                          <SelectItem key={p._id} value={p._id} className="text-white hover:bg-[#27272A]">{p.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {formData.professor_ids.length === 0 && (
                    <p className="text-xs text-yellow-400">Marque ao menos um professor acima para poder definir o presidente.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Logo da Equipe</Label>
                  <div className="flex items-start gap-4">
                    <ImageUpload
                      entityType="team"
                      entityId={editingTeam?._id}
                      currentPath={formData.logo_path}
                      onUpload={handleLogoUpload}
                      shape="square"
                      label="Upload Logo"
                    />
                    <p className="text-xs text-[#71717A] mt-2">
                      Logo quadrado recomendado (200x200 pixels)
                    </p>
                  </div>
                </div>
              </form>
              {/* Sticky footer (always visible — submits the inner form by id) */}
              <BarraAcoes contexto="dialogo">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setIsDialogOpen(false); resetForm(); }}
                  className="border-[#27272A] text-[#A1A1AA]"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  data-testid="save-team"
                  className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingTeam ? 'Salvar' : 'Criar')}
                </Button>
              </BarraAcoes>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filtros em faixa própria (Etapa Visual 2) */}
        {/* PEDIDOS DE EQUIPE (fila de quem aprova) */}
        {canManage && solicitacoes.length > 0 && (
          <div className="rounded-lg border border-[#D4AF37]/30 bg-[#141414] p-4 space-y-3" data-testid="fila-pedidos-equipe">
            <div className="flex items-center gap-2 micro-rotulo">
              <ShieldPlus size={12} /> Pedidos de equipe ({solicitacoes.length})
            </div>
            {solicitacoes.map((s) => (
              <div key={s._id} className="rounded border border-[#27272A] bg-[#0A0A0A] p-3" data-testid={`pedido-${s._id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-white">{s.name}</p>
                    <p className="text-micro text-[#71717A]">
                      {[s.city, s.state].filter(Boolean).join('/') || 'sem cidade'} · pedido por {s.requested_by_name || s.requested_by_email || '—'}
                    </p>
                    {s.observation && <p className="text-micro text-[#A1A1AA] mt-1">{s.observation}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => aprovarPedido(s._id)} disabled={processando === s._id}
                            data-testid={`aprovar-${s._id}`}
                            className="bg-green-600 text-white hover:bg-green-700">
                      {processando === s._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check size={14} className="mr-1" />}
                      Aprovar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRecusando(s)} disabled={processando === s._id}
                            data-testid={`recusar-${s._id}`}
                            className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                      <X size={14} className="mr-1" /> Recusar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <FaixaFiltros rotulo="Buscar equipes">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={20} />
          <Input
            placeholder="Buscar equipes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-teams"
            className="pl-10 bg-[#141414] border-[#27272A] text-white placeholder:text-[#71717A]"
          />
        </div>
        </FaixaFiltros>

        {/* Teams Grid */}
        {loading ? (
          <EstadoCarregando />
        ) : filteredTeams.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio
                icone={Shield}
                titulo="Nenhuma equipe encontrada"
                descricao="Crie sua primeira equipe para começar"
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTeams.map((team) => (
              <Card 
                key={team._id}
                className="bg-[#141414] border-[#27272A] card-hover"
                data-testid={`team-card-${team._id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                        <Shield className="w-6 h-6 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{team.name}</h3>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    {team.address && (
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <MapPin size={16} className="text-[#D4AF37]" />
                        <span className="truncate">{team.address}</span>
                      </div>
                    )}
                    {team.total_medals && (
                      <div className="flex items-center gap-4 text-[#A1A1AA]">
                        <span className="flex items-center gap-1">
                          <Medal size={16} className="medalha-ouro" /> {team.total_medals.gold || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Medal size={16} className="medalha-prata" /> {team.total_medals.silver || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Medal size={16} className="medalha-bronze" /> {team.total_medals.bronze || 0}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {canManage && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(team)}
                          className="flex-1 border-[#27272A] text-[#A1A1AA] hover:text-white"
                        >
                          <Edit size={16} className="mr-1" /> Editar
                        </Button>
                        {/* Separador (Fase 2, item 10): a exclusão não fica colada
                            na ação normal — evita apagar por engano. */}
                        <div className="w-px self-stretch bg-[#27272A]" aria-hidden="true" />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(team._id)}
                          aria-label="Excluir"
                          className="border-[#27272A] text-red-400 hover:text-red-300 hover:border-red-400/50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <DialogoMotivo
        aberto={!!recusando}
        titulo="Recusar pedido de equipe"
        descricao={recusando ? `Pedido de "${recusando.name}" — o líder verá o motivo e poderá enviar de novo.` : ''}
        rotulo="Motivo da recusa *"
        placeholder="Ex.: nome da equipe já existe / dados incompletos..."
        textoConfirmar="Recusar pedido"
        varianteConfirmar="destructive"
        aoConfirmar={recusarPedido}
        aoCancelar={() => setRecusando(null)}
        testId="recusar-pedido-equipe"
      />
    </MainLayout>
  );
}
